import {
  ConfigFormItem,
  getWidgetProps,
  IWidgetComponentProps,
  RichTextAdvancedRender,
} from '@framework/engine';
import { DraggerInput, UploadInput } from '@framework/form';
import { message, Modal } from 'antd';
import { RcFile } from 'antd/es/upload';
import { useCallback, useRef } from 'react';
import { useController, useWatch } from 'react-hook-form';
import { AiFileCheckMessage } from '../../../../ai/AiFileCheckMessage';
import { IUploadWidget, UploadWidgetKind } from '../IUploadWidget';
import { UploadedFileRow } from './UploadedFileRow';
import { useUploadWidget } from './useUploadWidget';

const B_TO_MB = 1024 * 1024; // 1MB = 1024 KB = 1024*1024 B

const focusOnUploadButton = (id: string) => {
  const buttonElement = document.getElementById(id);
  setTimeout(() => {
    if (buttonElement) {
      buttonElement?.focus();
    }
  }, 500);
};

export interface IUploadWidgetComponentInputProps
  extends IWidgetComponentProps<IUploadWidget> {
  /**
   * Se true, indica che l'input sarà per il file "pubblico",
   * necessario nel caso di widget con campi sensibili
   * (direttiva omissis true).
   */
  publicUpload?: boolean;

  attachmentUniqueId?: string;
}

export function UploadWidgetComponentInput(
  props: IUploadWidgetComponentInputProps,
) {
  const { widget, publicUpload, attachmentUniqueId } = props;
  const options = getWidgetProps(props);

  const isButton = widget.kind === UploadWidgetKind.Button;

  //TODO: implement file type Testo da mostrare
  // const accept = widget.rules?.fileType?.value.join(', ') ?? undefined;

  const {
    title,
    label,
    helpText,
    fieldName,
    filenameField,
    fileIdField,
    sectionId,
    maxFileSize,
    maxFileSizeMessage,
    fileTypes,
    submissionId,
    submissionModuleId,
    upload,
    verifyDocumentAiField,
    verifyDocumentAiFieldName,
    fileList,
    setFileList,
  } = useUploadWidget({ widget, options, publicUpload });

  const verifyDocumentAiValue = useWatch({
    name: verifyDocumentAiFieldName,
  });

  const handleCustomRequest = useCallback(
    (options: any) => {
      const { file, onError } = options;

      if (!file) {
        console.error('[Upload] Error: file not found');
        return;
      }

      if (!submissionId || !submissionModuleId) {
        return;
      }

      // Se il widget è configurato come sensibile, bisogna informare l'API di upload.
      const prevedeDatiSensibili = widget.directives?.omissis?.enabled;

      upload
        .mutateAsync({
          id: submissionId,
          codice: widget.code,
          data: { file: file },
          idRichiestaModulo: submissionModuleId,
          idSezione: sectionId,
          params: {
            // Indica se il widget necessita di documento pubblico/privato
            prevedeDatiSensibili,
            // Indica se il file che si sta caricando è pubblico o privato
            omissis: publicUpload,
            idAllegatoPrecedente: attachmentUniqueId,
          },
        })
        .then(uploadResult => {
          setFileList([
            { ...file, uid: uploadResult.idFile, name: uploadResult.nomeFile },
          ]);
          filenameField.setValue(uploadResult.nomeFile);
          fileIdField.setValue(uploadResult.idFile);
          verifyDocumentAiField.setValue(uploadResult.verificaDocumentoAi);
          message.success('File caricato con successo');
        })
        .catch(err => {
          console.error('[Upload] Error after API', err);
          onError?.(err);
          message.error(
            err.response?.data?.descrizione ??
              'Errore durante il caricamento del file',
          );
        });
    },
    [
      submissionId,
      submissionModuleId,
      widget.directives?.omissis?.enabled,
      widget.code,
      upload,
      sectionId,
      publicUpload,
      attachmentUniqueId,
      setFileList,
      filenameField,
      fileIdField,
      verifyDocumentAiField,
    ],
  );

  const handleBeforeUpload = useCallback(
    (file: RcFile) => {
      focusOnUploadButton(fieldName + '-button-focus-id');

      // FILE NAME CHECK
      // Only letters, number, "-", "_", " " and the last "." are allowed
      const regex = /^[a-zA-Z0-9-_. ]+$/;

      // Come da ticket https://trello.com/c/XOt8ox3F/388-fo-comp-fwp-542-verifica-nome-file-caricati
      // unifichiamo il messaggio d'errore utilizzando lo stesso anche nel caso in cui ci siano 2 o più punti nel nome del file.
      if (!regex.test(file.name) || file.name.split('.').length > 2) {
        message.error(
          `Il nome file "${file.name}" non è valido. Sono ammessi solo numeri, lettere, spazi, "-" e "_".`,
        );
        return false;
      }

      // SIZE CHECK
      if (maxFileSize > 0 && file.size / B_TO_MB > maxFileSize) {
        const fileSize = Math.ceil((file.size / B_TO_MB) * 100) / 100;
        message.error(
          maxFileSizeMessage ??
            `Il file ${file.name} è troppo grande (${fileSize} MB). Dimensione massima: ${maxFileSize} MB`,
        );
        return false;
      }

      return true;
    },
    [maxFileSize, maxFileSizeMessage],
  );

  const confirmed = useRef(false);

  const {
    field: { ref: fieldRef },
  } = useController({ name: fieldName });

  const hasSubmissionIds = Boolean(submissionId && submissionModuleId);

  const isRequired = widget.rules?.required?.value as boolean;

  return (
    <ConfigFormItem
      name={fieldName}
      label={<RichTextAdvancedRender text={label} />}
      help={isButton && <RichTextAdvancedRender text={helpText} />}
      layout={{ hideVisualLabel: options.hideLabel }}
      readonly={options.readonly}
    >
      {isButton ? (
        <UploadInput
          srFieldLabel={<RichTextAdvancedRender text={label} />}
          disabled={!hasSubmissionIds}
          accept={fileTypes.join(',')}
          name={fieldName}
          title={title}
          ref={fieldRef}
          multiLineButton
          buttonProps={{
            // per accessibilità
            'aria-required': isRequired,
            id: fieldName + '-button-focus-id',

            loading: upload.isLoading,
            onClick: e => {
              // Se è già presente un file e non è stato confermato il sovrascrivere
              // chiediamo conferma con una modal
              if (fileList.length >= 1 && !confirmed.current) {
                Modal.confirm({
                  title: 'Attenzione',
                  content:
                    "E' possibile caricare un solo file. Vuoi sovrascrivere il file caricato?",
                  okText: 'Sovrascrivi',
                  cancelText: 'Annulla',
                  onOk: () => {
                    // Confermo la sovrascrittura e rilancio l'evento
                    // in questo modo è come se "ripremessi" il pulsante,
                    // ma questa volta l'if verrà saltato (confirmed = true) e
                    // si avvierà l'upload
                    confirmed.current = true;
                    e.target.dispatchEvent(e.nativeEvent);
                  },
                });
                e.stopPropagation();
              }

              // Se arrivo qui o non ci sono file o è stata confermata la sovrascrittura
              // reimposto il flag e continuo (l'event non viene stoppato e arriva all'uploadInput)
              confirmed.current = false;
            },
          }}
          customRequest={handleCustomRequest}
          beforeUpload={handleBeforeUpload}
          fileList={fileList}
          showUploadList={false}
        />
      ) : (
        <DraggerInput
          name={options.name}
          title={title}
          info={helpText ? <RichTextAdvancedRender text={helpText} /> : <div />}
          customRequest={handleCustomRequest}
          beforeUpload={handleBeforeUpload}
          fileList={fileList}
          disabled={!hasSubmissionIds}
        />
      )}
      {fileList && fileList.length > 0 && (
        <div>
          {fileList.map(file => (
            <UploadedFileRow
              key={file.uid}
              file={file}
              isFileSigned={widget.rules?.needSign?.value ?? false}
              submissionId={submissionId}
              submissionModuleId={submissionModuleId}
              sectionId={sectionId}
              code={widget.code}
              reset={() => {
                setFileList([]);
                fileIdField.setValue(null);
                filenameField.setValue(null);
                verifyDocumentAiField.setValue(null);
                // Se il file è stato rimosso, rifocalizzo il pulsante
                focusOnUploadButton(fieldName + '-button-focus-id');
              }}
              omissis={publicUpload}
              extra={
                <AiFileCheckMessage
                  result={verifyDocumentAiValue}
                  isBackoffice={undefined}
                  isReadonly={options.readonly}
                />
              }
              isImage={widget.isImage}
            />
          ))}
        </div>
      )}
    </ConfigFormItem>
  );
}
